import { usePunditUserContext } from "react/contexts/punditUserContext";
import { useCircleV4OptIn } from "react/hooks/useCircleV4OptIn";

import { t } from "@/i18n-js/instance";

import { useV4Activation } from "../ActivationConfirmationModal/useV4Activation";
import {
  ACTIVATION_LOADING_MIN_DURATION_MS,
  ECLIPSE_MARKETING_URL,
  ECLIPSE_MARKETING_URL_WITH_UTM_PARAMS,
  isFromEclipseMidnightToKeynoteStart,
  isKeynoteLive,
  isOptInBarDismissible,
  isPostKeynote,
} from "../constants";

const locale = (key: string) => t(`circle_v4.opt_in_banner.${key}`);

interface OptInBarContent {
  ctaExternalUrl?: string;
  isCTAEnabled?: boolean;
  onCTAClick?: () => void;
  optInCTAContent: string;
  optInText: string;
}

const getKeynoteLiveBarContent = (): OptInBarContent => ({
  optInText: locale("opt_in_text"),
  optInCTAContent: locale("watch_now_button_text"),
  ctaExternalUrl: ECLIPSE_MARKETING_URL_WITH_UTM_PARAMS,
});

const getWaitlistBarContent = (
  isWaitlistJoined: boolean,
  isPostKeynotePhase: boolean,
): OptInBarContent => {
  if (isPostKeynotePhase) {
    if (isWaitlistJoined) {
      return {
        optInText: locale("post_keynote_waitlist_text"),
        optInCTAContent: locale("waitlist_button_text"),
      };
    }

    return {
      optInText: locale("opt_in_text"),
      optInCTAContent: locale("waitlist_button_text"),
    };
  }

  if (isWaitlistJoined) {
    return {
      optInText: isFromEclipseMidnightToKeynoteStart()
        ? locale("keynote_on_waitlist_text")
        : locale("post_waitlist_text"),
      optInCTAContent: locale("waitlist_button_text"),
      ctaExternalUrl: `${ECLIPSE_MARKETING_URL}?confirmation=1`,
    };
  }

  return {
    optInText: locale("rsvp_text"),
    optInCTAContent: locale("waitlist_button_text"),
  };
};

const getOptInBarContent = (): OptInBarContent => ({
  optInText: locale("opt_in_text"),
  optInCTAContent: locale("opt_in_button_text"),
});

export const useOptInBarContent = ():
  | (OptInBarContent & { onDismiss?: () => void })
  | null => {
  const { dismissOptInBar, shouldShowOptInBar, shouldShowWaitlistBar } =
    useCircleV4OptIn();
  const { mutate: enablePreview, isActivating: isEnablingPreview } =
    useV4Activation({
      showPreviewModal: true,
      showActivationLoadingScreen: true,
      redirectTo: "/ai",
      minimumLoadingDurationMs: ACTIVATION_LOADING_MIN_DURATION_MS,
    });

  const { currentCommunity } = usePunditUserContext();
  const isWaitlistJoined = Boolean(currentCommunity?.joined_v4_waitlist_at);
  const onDismiss = isOptInBarDismissible() ? dismissOptInBar : undefined;

  if (!shouldShowOptInBar && !shouldShowWaitlistBar) {
    return null;
  }

  if (shouldShowOptInBar) {
    return {
      ...getOptInBarContent(),
      isCTAEnabled: !isEnablingPreview,
      onCTAClick: enablePreview,
    };
  }

  if (isKeynoteLive()) {
    return getKeynoteLiveBarContent();
  }

  if (shouldShowWaitlistBar) {
    return {
      ...getWaitlistBarContent(isWaitlistJoined, isPostKeynote()),
      onDismiss,
    };
  }

  return getOptInBarContent();
};
